import type { GameSettings } from "./types";

type SoundName =
  | "click"
  | "success"
  | "error"
  | "step"
  | "pickup"
  | "alert"
  | "boot"
  | "coffee";

export class AudioManager {
  private context?: AudioContext;
  private master?: GainNode;
  private musicTimer?: number;
  private stepToggle = false;

  constructor(private readonly settings: () => GameSettings) {}

  unlock(): void {
    if (!this.context) {
      try {
        this.context = new AudioContext();
        this.master = this.context.createGain();
        this.master.gain.value = 0.24;
        this.master.connect(this.context.destination);
      } catch {
        return;
      }
    }
    if (this.context.state === "suspended") {
      void this.context.resume();
    }
  }

  play(name: SoundName): void {
    if (!this.settings().sound) return;
    this.unlock();

    const presets: Record<SoundName, [number, number, OscillatorType, number]> = {
      click: [520, 690, "square", 0.055],
      success: [440, 880, "sine", 0.22],
      error: [180, 90, "sawtooth", 0.2],
      step: [this.stepToggle ? 90 : 105, 62, "triangle", 0.04],
      pickup: [330, 990, "triangle", 0.18],
      alert: [740, 370, "square", 0.28],
      boot: [110, 660, "sawtooth", 0.5],
      coffee: [150, 260, "sine", 0.35],
    };

    this.stepToggle = !this.stepToggle;
    const [from, to, type, duration] = presets[name];
    this.tone(from, to, type, duration, name === "step" ? 0.035 : 0.12);
  }

  startMusic(): void {
    this.stopMusic();
    if (!this.settings().music) return;
    this.unlock();

    const notes = [110, 146.83, 164.81, 220, 196, 146.83, 123.47, 164.81];
    let index = 0;
    this.musicTimer = window.setInterval(() => {
      if (!this.settings().music || document.hidden) return;
      const note = notes[index % notes.length];
      this.tone(note, note * 1.004, "triangle", 0.32, 0.025);
      if (index % 4 === 0) {
        this.tone(note / 2, note / 2, "sine", 0.55, 0.018);
      }
      index += 1;
    }, 380);
  }

  stopMusic(): void {
    if (this.musicTimer) {
      window.clearInterval(this.musicTimer);
      this.musicTimer = undefined;
    }
  }

  private tone(
    from: number,
    to: number,
    type: OscillatorType,
    duration: number,
    volume: number,
  ): void {
    if (!this.context || !this.master) return;
    const now = this.context.currentTime;
    const oscillator = this.context.createOscillator();
    const gain = this.context.createGain();
    oscillator.type = type;
    oscillator.frequency.setValueAtTime(from, now);
    oscillator.frequency.exponentialRampToValueAtTime(Math.max(20, to), now + duration);
    gain.gain.setValueAtTime(0.0001, now);
    gain.gain.exponentialRampToValueAtTime(volume, now + 0.012);
    gain.gain.exponentialRampToValueAtTime(0.0001, now + duration);
    oscillator.connect(gain);
    gain.connect(this.master);
    oscillator.start(now);
    oscillator.stop(now + duration + 0.02);
  }
}
